import {
	type ContingentRoomRef,
	HELPDESK_CONTACT_NAME_MAX,
	HELPDESK_CONTACT_PHONE_MAX,
	HELPDESK_DETAILS_MAX,
	HELPDESK_PRIORITIES,
	HELPDESK_PRIORITY_DEFAULT,
	HELPDESK_TITLE_MAX,
	LODGING_BLOCK_MAX,
	LODGING_ROOM_NO_MAX,
	formatTicketNo,
} from "@seagames/contracts";
import { Field, Modal, ModalActions, Select } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../lib/api/client";
import { buildingOptionsOptions } from "../../lib/api/accommodation";
import { createHelpdeskTicket, invalidateHelpdesk } from "../../lib/api/helpdesk";
import { responsibleOrganizationOptionsOptions } from "../../lib/responsible-organizations/queries";
import { toast } from "../../lib/ui/toast";
import { CheckIcon } from "../transport/icons";
import { useHelpdeskSchemas } from "./schemas";
import type { CreateHelpdeskTicketBody, HelpdeskPriority } from "./types";

/** A room a NOC's own people stay in — the only rooms it may report (PLAN A13). */
export type ReportableRoom = ContingentRoomRef;

type Errors = Partial<Record<"title" | "building" | "room" | "details" | "contact", string>>;

/** A schema issue's first path segment → the form field that shows it. */
const FIELD_OF: Record<string, keyof Errors> = {
	title: "title",
	lodging_building_id: "building",
	lodging_room_id: "room",
	room_no: "room",
	details: "details",
	contact_name: "contact",
	contact_phone: "contact",
};

const inputClass = (bad: boolean) => `input w-full ${bad ? "input-error" : ""}`;

/**
 * New ticket — two forms, one sheet:
 *   desk  her `tkSheet` new: a room problem phoned in or reported at the
 *         desk (host desk, a HOTEL). Building required; the room is typed
 *         ("A-412") and the API resolves it inside that building.
 *   noc   her `nTkSheet` "Report a room problem": a NOC picks one of the
 *         rooms its OWN people stay in (never a list of the building), and
 *         the ticket goes to the host's accommodation team.
 * Category is ROOM in M11 (M13 routes the others).
 */
export function NewTicketModal(
	props:
		| { mode: "desk"; gamesId: string; onClose: () => void; fixedBuildingIds?: readonly string[] }
		| { mode: "noc"; gamesId: string; onClose: () => void; organizationId: string; rooms: readonly ReportableRoom[]; defaultContact: string },
) {
	const { t } = useTranslation("helpdesk");
	const { mode, gamesId, onClose } = props;
	const firstField = useRef<HTMLInputElement>(null);
	const schemas = useHelpdeskSchemas();
	const buildings = useQuery(buildingOptionsOptions(gamesId, mode === "desk"));
	const orgs = useQuery({ ...responsibleOrganizationOptionsOptions(), enabled: mode === "desk" });

	const deskBuildings = (buildings.data ?? []).filter((b) => mode !== "desk" || !props.fixedBuildingIds || props.fixedBuildingIds.includes(b.lodging_building_id));
	const [title, setTitle] = useState("");
	const [priority, setPriority] = useState<HelpdeskPriority>(HELPDESK_PRIORITY_DEFAULT);
	const [orgId, setOrgId] = useState<string | null>(null);
	const [buildingId, setBuildingId] = useState<string | null>(null);
	const [roomNo, setRoomNo] = useState("");
	const [roomId, setRoomId] = useState<string | null>(null);
	const [contact, setContact] = useState(mode === "noc" ? props.defaultContact : "");
	const [phone, setPhone] = useState("");
	const [details, setDetails] = useState("");
	const [errors, setErrors] = useState<Errors>({});
	const [saving, setSaving] = useState(false);

	// One building to choose from (a HOTEL with one building): it is the answer.
	const building = buildingId ?? (deskBuildings.length === 1 ? (deskBuildings[0]?.lodging_building_id ?? null) : null);

	const submit = async () => {
		const room = mode === "noc" ? props.rooms.find((r) => r.lodging_room_id === roomId) : undefined;
		if (mode === "noc" && !room) {
			setErrors({ room: t("validation.roomRequired") });
			return;
		}
		// One shape for both forms; the contract's schema (localized) is the check, as on the API.
		const parsed = schemas.create.safeParse({
			games_id: gamesId,
			category: "ROOM",
			priority,
			title,
			details: details || null,
			lodging_building_id: mode === "noc" ? (room?.lodging_building_id ?? null) : building,
			lodging_room_id: mode === "noc" ? (room?.lodging_room_id ?? null) : null,
			// `.trim().min(1)` refuses "": an empty field is null.
			room_no: mode === "desk" && roomNo.trim() ? roomNo.trim() : null,
			responsible_organization_id: mode === "noc" ? props.organizationId : orgId,
			contact_name: contact || null,
			contact_phone: phone || null,
		} satisfies CreateHelpdeskTicketBody);
		if (!parsed.success) {
			const next: Errors = {};
			for (const issue of parsed.error.issues) {
				const field = FIELD_OF[String(issue.path[0])];
				if (field && !next[field]) next[field] = issue.message;
			}
			setErrors(next);
			return;
		}
		setErrors({});
		const body: CreateHelpdeskTicketBody = parsed.data;
		setSaving(true);
		try {
			const created = await createHelpdeskTicket(body);
			await invalidateHelpdesk();
			toast.success(mode === "noc" ? t("report.sent", { ticket: formatTicketNo(created.ticket_no) }) : t("create.created", { ticket: formatTicketNo(created.ticket_no) }));
			onClose();
		} catch (err) {
			setSaving(false);
			// An unknown or ambiguous typed room: 422 with `details.fieldErrors.room_no` — say so on the field.
			// The envelope is snake_case (`ok()` / the error contract): `details.field_errors.room_no`, `details.field`.
			const details = err instanceof ApiError ? (err.details as { field?: string; field_errors?: Record<string, unknown> } | undefined) : undefined;
			if (err instanceof ApiError && err.code === "VALIDATION_FAILED" && (details?.field_errors?.room_no || details?.field === "room_no")) {
				setErrors({ room: t("validation.roomNotFound", { room: roomNo.trim() }) });
				return;
			}
			toast.error(err, mode === "noc" ? t("report.failed") : t("create.failed"));
		}
	};

	const priorities = HELPDESK_PRIORITIES.map((p) => ({ value: p, label: t(`priority.label.${p}`) }));

	return (
		<Modal
			open
			onClose={onClose}
			size="md"
			title={mode === "noc" ? t("report.title") : t("create.title")}
			description={mode === "noc" ? t("report.description") : t("create.description")}
			dismissible={!saving}
			initialFocusRef={firstField}
			footer={
				<ModalActions>
					<button type="button" className="btn btn-sm btn-line" onClick={onClose} disabled={saving}>
						{t("common.cancel")}
					</button>
					<button type="submit" form="hd-new-ticket" className="btn btn-sm btn-primary" disabled={saving}>
						{saving ? <span className="loading loading-spinner loading-xs" /> : <CheckIcon className="size-[0.9375rem]" />}
						{mode === "noc" ? t("report.submit") : t("create.submit")}
					</button>
				</ModalActions>
			}
		>
			<form
				id="hd-new-ticket"
				noValidate
				className="grid gap-[0.875rem]"
				onSubmit={(e) => {
					e.preventDefault();
					if (!saving) void submit();
				}}
			>
				<Field label={mode === "noc" ? t("report.whatIsWrong") : t("create.problem")} htmlFor="hd-title" required error={errors.title}>
					<input
						id="hd-title"
						ref={firstField}
						value={title}
						maxLength={HELPDESK_TITLE_MAX}
						autoComplete="off"
						placeholder={t("create.problemPlaceholder")}
						onChange={(e) => setTitle(e.target.value)}
						className={inputClass(!!errors.title)}
					/>
				</Field>

				{mode === "noc" ? (
					<div className="grid gap-[0.75rem] sm:grid-cols-2">
						<Field label={t("report.room")} htmlFor="hd-room" required error={errors.room} hint={props.rooms.length === 0 ? t("report.noRooms") : undefined}>
							<Select
								id="hd-room"
								options={props.rooms.map((r) => ({ value: r.lodging_room_id, label: t("report.roomOption", { building: r.building_name, room: r.room_no_display }) }))}
								value={roomId}
								onChange={setRoomId}
								placeholder={t("common.choose")}
								searchPlaceholder={t("common.search")}
								emptyMessage={t("common.noMatches")}
								disabled={saving || props.rooms.length === 0}
							/>
						</Field>
						<Field label={t("report.howUrgent")} htmlFor="hd-priority" required>
							<Select id="hd-priority" searchable={false} options={priorities} value={priority} onChange={(v) => v && setPriority(v as HelpdeskPriority)} disabled={saving} />
						</Field>
					</div>
				) : (
					<>
						<div className="grid gap-[0.75rem] sm:grid-cols-2">
							<Field label={t("create.contingent")} htmlFor="hd-org" aside={t("common.optional")}>
								<Select
									id="hd-org"
									clearable
									options={(orgs.data ?? []).map((o) => ({ value: o.responsible_organization_id, label: o.name }))}
									value={orgId}
									onChange={setOrgId}
									placeholder={t("common.choose")}
									searchPlaceholder={t("common.search")}
									emptyMessage={t("common.noMatches")}
									disabled={saving}
								/>
							</Field>
							<Field label={t("ticket.priority")} htmlFor="hd-priority" required>
								<Select id="hd-priority" searchable={false} options={priorities} value={priority} onChange={(v) => v && setPriority(v as HelpdeskPriority)} disabled={saving} />
							</Field>
						</div>
						<div className="grid gap-[0.75rem] sm:grid-cols-2">
							<Field label={t("create.building")} htmlFor="hd-building" required error={errors.building}>
								<Select
									id="hd-building"
									options={deskBuildings.map((b) => ({ value: b.lodging_building_id, label: b.name }))}
									value={building}
									onChange={setBuildingId}
									placeholder={buildings.isPending ? t("common.loading") : t("common.choose")}
									searchPlaceholder={t("common.search")}
									emptyMessage={t("common.noMatches")}
									disabled={saving || deskBuildings.length <= 1}
								/>
							</Field>
							<Field label={t("create.roomNumber")} htmlFor="hd-room-no" aside={t("common.optional")} error={errors.room}>
								<input
									id="hd-room-no"
									value={roomNo}
									maxLength={LODGING_BLOCK_MAX + 1 + LODGING_ROOM_NO_MAX}
									autoComplete="off"
									placeholder={t("create.roomNumberPlaceholder")}
									onChange={(e) => setRoomNo(e.target.value)}
									className={inputClass(!!errors.room)}
								/>
							</Field>
						</div>
					</>
				)}

				<div className="grid gap-[0.75rem] sm:grid-cols-2">
					<Field label={t("create.contactName")} htmlFor="hd-contact" aside={t("common.optional")}>
						<input id="hd-contact" value={contact} maxLength={HELPDESK_CONTACT_NAME_MAX} autoComplete="off" onChange={(e) => setContact(e.target.value)} className={inputClass(false)} />
					</Field>
					<Field label={t("create.contactPhone")} htmlFor="hd-phone" aside={t("common.optional")}>
						<input id="hd-phone" type="tel" value={phone} maxLength={HELPDESK_CONTACT_PHONE_MAX} autoComplete="off" onChange={(e) => setPhone(e.target.value)} className={inputClass(false)} />
					</Field>
				</div>

				<Field label={mode === "noc" ? t("report.details") : t("create.notes")} htmlFor="hd-details" aside={t("common.optional")} error={errors.details}>
					<textarea
						id="hd-details"
						rows={3}
						value={details}
						maxLength={HELPDESK_DETAILS_MAX}
						onChange={(e) => setDetails(e.target.value)}
						placeholder={mode === "noc" ? undefined : t("create.notesPlaceholder")}
						className={`textarea w-full ${errors.details ? "textarea-error" : ""}`}
						disabled={saving}
					/>
				</Field>
			</form>
		</Modal>
	);
}
